import { createAsyncThunk } from '@reduxjs/toolkit';
import config from 'config';
import { enqueueSnackbar } from 'notistack';
import type { JobInvitationBody, ProfileMatchParam } from 'src/openapi';
import {
  resumeBlobFromResponse,
  resumeDownloadName,
} from 'src/helpers/resumeFile';
import { getFormattedErr } from 'src/helpers';
import axios from 'axios';
import apiClient from 'src/helpers/apiClient';
import type { RootState } from '..';

export const fetchInviteProfileList = createAsyncThunk(
  'get/invite-profile-list',
  async (
    {
      jobId,
      orgId,
      payload,
    }: {
      jobId: string;
      orgId: string;
      payload: ProfileMatchParam;
      selectedProfileId?: string;
    },
    { rejectWithValue, signal },
  ) => {
    try {
      const res = await (
        await apiClient.getJobApi()
      ).jobApiGetJobProfileMatch(
        { jobId, orgId, profileMatchParam: payload },
        { signal },
      );
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const fetchDbAccessStatus = createAsyncThunk(
  'get/db-access-status',
  async (
    { jobId, orgId }: { jobId: string; orgId: string },
    { rejectWithValue, signal },
  ) => {
    try {
      const res = await (
        await apiClient.getJobApi()
      ).jobApiGetDbAccessStatus({ jobId, orgId }, { signal });
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const unlockDbAccess = createAsyncThunk(
  'post/unlock-db-access',
  async (
    { jobId, orgId }: { jobId: string; orgId: string },
    { rejectWithValue },
  ) => {
    try {
      const res = await (
        await apiClient.getJobApi()
      ).jobApiUnlockDbAccess({ jobId, orgId });
      return res.data;
    } catch (err: any) {
      const errorKey = err?.response?.data?.error;
      // Insufficient credits opens the buy-credits dialog in-slice; suppress
      // the generic snackbar for it (mirrors unlockApplicant / requestBulkAnalysis).
      if (errorKey !== 'insufficient_employer_credits') {
        enqueueSnackbar(getFormattedErr(errorKey), { variant: 'error' });
      }
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const fetchInviteProfileDetails = createAsyncThunk(
  'fetch/invite-applicant-details',
  async (
    {
      profileId,
      jobId,
      orgId,
      showContacts = false,
    }: {
      profileId: string;
      jobId: string;
      orgId: string;
      showContacts?: boolean;
    },
    { rejectWithValue },
  ) => {
    try {
      const res = await (
        await apiClient.getJobApi()
      ).jobApiGetJobProfileMatchById({ jobId, orgId, profileId, showContacts });
      return res.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const unlockInviteProfile = createAsyncThunk(
  'post/unlock-invite-profile',
  async (
    {
      jobId,
      orgId,
      profileId,
    }: { jobId: string; orgId: string; profileId: string },
    { rejectWithValue },
  ) => {
    try {
      const res = await (
        await apiClient.getJobApi()
      ).jobApiUnlockInviteProfile({ jobId, orgId, profileId });
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const rejectJobProfile = createAsyncThunk(
  'reject-profile',
  async (
    {
      jobId,
      orgId,
      profileId,
    }: { jobId: string; orgId: string; profileId: string },
    { rejectWithValue },
  ) => {
    try {
      const res = await (
        await apiClient.getJobApi()
      ).jobApiRejectJobProfileByEmployer({ jobId, orgId, profileId });
      return res.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const sendJobInvite = createAsyncThunk(
  'post/send-invitation',
  async (
    {
      jobId,
      orgId,
      profileId,
      payload,
    }: {
      jobId: string;
      orgId: string;
      profileId: string;
      payload: JobInvitationBody;
    },
    { rejectWithValue },
  ) => {
    try {
      const res = await (
        await apiClient.getJobApi()
      ).jobApiInviteUsersJob({
        jobId,
        orgId,
        profileId,
        jobInvitationBody: payload,
      });
      return res.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const downloadDBResume = createAsyncThunk(
  'get/download-db-resume',
  async (
    {
      orgId,
      jobId,
      profileId,
    }: { orgId: string; jobId: string; profileId: string },
    { rejectWithValue, getState, fulfillWithValue },
  ) => {
    const url = `${config.API_URL}/org/${orgId}/job/${jobId}/profile/${profileId}/download`;
    const state = getState() as RootState;
    try {
      const response = await axios.get(url, {
        responseType: 'blob',
        headers: {
          Authorization: `Bearer ${state.auth?.token as string}`,
        },
      });

      const blob = resumeBlobFromResponse(response);
      const url1 = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url1;
      link.setAttribute('download', resumeDownloadName('resume', blob.type));
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url1);
      return fulfillWithValue({
        status: 'success',
        message: 'resume downloaded',
      });
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const getInvitedUserProfile = createAsyncThunk(
  'get/invited-user-profile',
  async (
    {
      orgId,
      jobId,
      profileId,
      showContact = false,
    }: {
      orgId: string;
      jobId: string;
      profileId: string;
      showContact?: boolean;
    },
    { rejectWithValue },
  ) => {
    try {
      const res = await (
        await apiClient.getJobApi()
      ).jobApiGetAndMarkJobProfileDownload({
        jobId,
        orgId,
        profileId,
        showContacts: showContact,
      });

      return res.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const downloadInviteOriginalResume = createAsyncThunk(
  'get/download-invite-original-resume',
  async (
    {
      orgId,
      jobId,
      profileId,
      applicantName,
    }: {
      orgId: string;
      jobId: string;
      profileId: string;
      applicantName?: string;
    },
    { rejectWithValue, fulfillWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiDownloadJobProfileMatchById(
        { jobId, orgId, profileId },
        { responseType: 'blob' },
      );

      const blob = resumeBlobFromResponse(response);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        resumeDownloadName(
          applicantName ? `${applicantName} resume` : 'resume',
          blob.type,
        ),
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      return fulfillWithValue({
        status: 'success',
        message: 'resume downloaded',
      });
    } catch (err: any) {
      const errorMessage = JSON.parse(await err?.response?.data?.text()) ?? '';
      enqueueSnackbar(getFormattedErr(errorMessage));
      return rejectWithValue(errorMessage);
    }
  },
);
